import { cachedAssetWithoutIdbBlob, isFileBackedCachedAsset, type ICachedAsset } from '@lucimark/shared/models/asset'
import type { TDBService } from '@/services/DBService'
import type { IHlsVideoAsset, IUrlWidgetAsset } from '@/types'
import Emittery from 'emittery'
import { v4 as uuidv4 } from 'uuid'
import { addPlayerAssetCacheSentryBreadcrumb } from '@/helpers/addPlayerAssetCacheSentryBreadcrumb'
import {
  hybridHeaderImageMime,
  hybridHeaderVideoMime,
  hybridPlaybackFileHeaderBytes,
  hybridPlaybackFilePathIfPresent,
  hybridPlaybackFileReadable,
  hybridPlaybackFileStat,
  readHybridPlaybackFileAsBlob
} from '@/helpers/androidHybridPlayback'
import { assessCachedBinaryForPlayback } from '@/helpers/cachedBinaryAssetPlaybackHealth'
import { mapAllSettledWithConcurrency } from '@/helpers/mapWithConcurrency'
import { ApiClientServiceError, type TAPIClientService } from '@/services/APIClientService'

export type TWidgetAssetMetadata = Record<string, { urlTemplate: string, width?: number, height?: number, singletonCapable?: boolean, paramKeysOmitWhenEmpty?: string[], paramKeysFlag?: string[], fixedDefaultConfig?: Record<string, unknown>, defaultConfig?: Record<string, unknown> }>
export type THlsVideoAssetMetadata = Record<string, { hlsUrl: string, posterUrl?: string }>

export interface IAssetService {
  fetchAssets: (
    urns: string[],
    widgetAssetMetadata?: TWidgetAssetMetadata,
    hlsVideoAssetMetadata?: THlsVideoAssetMetadata,
    opts?: TFetchAssetsOptions
  ) => Promise<(ICachedAsset | IUrlWidgetAsset | IHlsVideoAsset)[]>
}

/** Options for {@link AssetsService.fetchAssets}. */
export type TFetchAssetsOptions = {
  /**
   * When true, conditional-GET (If-None-Match) every IndexedDB hit that has an etag.
   * Default false: trust healthy local cache so cold reload / schedule rematch is not blocked on
   * N sequential revalidation round-trips (concurrency 3). Wake / cache-repair passes true via
   * {@link TSetPlaybackScheduleOptions.forceAssetReload}.
   */
  revalidateCachedEtags?: boolean
  /**
   * When true, never hit the network: return IDB / hybrid-disk hits only (omit misses).
   * Soft-defer rematch uses this for non-head URNs so paint is not blocked on campaign tail download.
   */
  localOnly?: boolean
}

export enum AssetsServiceEvent {
  DOWNLOAD_PROGRESS = 'download-progress'
}

export interface AssetsServiceEventMap {
  [AssetsServiceEvent.DOWNLOAD_PROGRESS]: number
}

/**
 * Cap parallel campaign asset downloads so a large playlist does not saturate a
 * weak/flaky uplink (and trip the API 120/min rate limit all at once).
 */
export const ASSET_BINARY_FETCH_CONCURRENCY = 3
/** Extra fetch rounds after the first attempt (transient failures after sleep / wake). */
export const ASSET_BINARY_FETCH_EXTRA_ROUNDS = 3
/**
 * Backoff (ms) before each extra round — longer than the old 500/1500 budget so
 * brief offline windows and mid-transfer drops can recover without hammering.
 */
export const ASSET_BINARY_FETCH_RETRY_DELAYS_MS = [1000, 3000, 8000] as const

function sleepMs(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function cachedAssetLooksLikeVideo(asset: ICachedAsset): boolean {
  return `${asset.type} ${asset.blob?.type ?? ''}`.toLowerCase().includes('video')
}

function isRetryAfterAwareHttpError(reason: unknown): reason is ApiClientServiceError {
  return (
    reason instanceof ApiClientServiceError
    && (reason.httpStatus === 429 || reason.httpStatus === 503)
    && reason.retryAfterMs != null
  )
}

export default class AssetsService extends Emittery<AssetsServiceEventMap> implements IAssetService {
  private dbService: TDBService
  private apiClientService: TAPIClientService
  private _downloadProgressIndex: Record<string, Record<string, number>> = {}
  private _downloadProgress: number = Number.NaN

  constructor(params: { dbService: TDBService, apiClientService: TAPIClientService }) {
    super()
    const { dbService, apiClientService } = params
    this.dbService = dbService
    this.apiClientService = apiClientService
    void this.emit(AssetsServiceEvent.DOWNLOAD_PROGRESS, this._downloadProgress)
  }

  /**
   * Drop a leftover IDB Blob when hybrid-cache already holds the MP4 and P2 can rehydrate
   * on demote (`readHybridPlaybackFile`). Pre-P2 APKs keep the blob.
   */
  private async _dropRedundantIdbBlobIfNativeFileReady(
    cached: ICachedAsset,
    nativeFilePath: string
  ): Promise<ICachedAsset> {
    if (cached.blob == null && cached.nativeFilePath === nativeFilePath) {
      return cached
    }
    if (cached.blob != null && !(await hybridPlaybackFileReadable(cached.urn))) {
      return cached
    }
    const next = cachedAssetWithoutIdbBlob(cached, nativeFilePath)
    addPlayerAssetCacheSentryBreadcrumb({
      phase: 'drop_idb_blob',
      context: 'fetchAssets',
      urn: cached.urn,
      bytes: cached.blob?.size ?? 0
    })
    return this.dbService.saveCachedAsset(next)
  }

  /**
   * Rebuild an IDB row from an on-disk P2 hybrid-cache file (bare URN key).
   * Avoids re-GET when WebView IDB was wiped but `files/hybrid-cache/` survived.
   * Sniffs magic for MIME — native always names files `*.mp4` even for stills.
   * Late-ftyp / unreadable header still recovers large files as video/mp4 (P2 video path)
   * so rematch does not re-GET a clip that soft-defer already treated as ready.
   */
  private async _recoverCachedAssetFromHybridDisk(urn: string): Promise<ICachedAsset | null> {
    const stat = await hybridPlaybackFileStat(urn)
    if (stat == null) {
      return null
    }
    const recoverFileBackedVideo = async (type: 'video/mp4' | 'video/webm'): Promise<ICachedAsset | null> => {
      const path = await hybridPlaybackFilePathIfPresent(urn, stat.byteLength)
      if (path == null) {
        return null
      }
      const recovered: ICachedAsset = {
        urn,
        size: stat.byteLength,
        type,
        lastAccess: new Date(),
        nativeFilePath: path,
        etag: ''
      }
      addPlayerAssetCacheSentryBreadcrumb({
        phase: 'repair_mime',
        context: 'fetchAssets',
        urn,
        bytes: stat.byteLength
      })
      return this.dbService.saveCachedAsset(recovered)
    }
    const header = await hybridPlaybackFileHeaderBytes(urn)
    if (header == null || header.length === 0) {
      // Soft-defer ready used existence only; do not fall through to HTTP for large native files.
      return stat.byteLength >= 64 * 1024 ? recoverFileBackedVideo('video/mp4') : null
    }
    const videoMime = hybridHeaderVideoMime(header)
    if (videoMime != null) {
      return recoverFileBackedVideo(videoMime)
    }
    const imageMime = hybridHeaderImageMime(header)
    if (imageMime != null) {
      const blob = await readHybridPlaybackFileAsBlob(urn, imageMime)
      if (blob == null || blob.size <= 0) {
        return null
      }
      const recovered: ICachedAsset = {
        urn,
        size: blob.size,
        type: imageMime,
        lastAccess: new Date(),
        blob,
        etag: ''
      }
      addPlayerAssetCacheSentryBreadcrumb({
        phase: 'repair_mime',
        context: 'fetchAssets',
        urn,
        bytes: blob.size
      })
      return this.dbService.saveCachedAsset(recovered)
    }
    return stat.byteLength >= 64 * 1024 ? recoverFileBackedVideo('video/mp4') : null
  }

  private _updateDownloadProgress() {
    const assetProgressIndex = Object.values(this._downloadProgressIndex).reduce((accumulator, value) => {
      Object.entries(value).forEach(([urn, progress]) => {
        accumulator[urn] = progress
      })
      return accumulator
    }, {})
    const totalAssets = Object.keys(assetProgressIndex).length
    this._downloadProgress = Object.values(assetProgressIndex).reduce((accumulator, progress) => accumulator += progress, 0) / totalAssets
    void this.emit(AssetsServiceEvent.DOWNLOAD_PROGRESS, this._downloadProgress)
  }

  async fetchAssets(
    urns: string[],
    widgetAssetMetadata?: TWidgetAssetMetadata,
    hlsVideoAssetMetadata?: THlsVideoAssetMetadata,
    opts?: TFetchAssetsOptions
  ): Promise<(ICachedAsset | IUrlWidgetAsset | IHlsVideoAsset)[]> {
    const revalidateCachedEtags = opts?.revalidateCachedEtags === true
    const localOnly = opts?.localOnly === true
    const assets: (ICachedAsset | IUrlWidgetAsset | IHlsVideoAsset)[] = []
    const urlWidgetUrns = new Set(Object.keys(widgetAssetMetadata ?? {}))
    const hlsVideoUrns = new Set(Object.keys(hlsVideoAssetMetadata ?? {}))
    const assetsToDownload = new Set<string>()
    const assetsToRevalidate = new Map<string, ICachedAsset>()

    for (const urn of urns) {
      if (urlWidgetUrns.has(urn)) {
        const meta = widgetAssetMetadata![urn]
        if (meta?.urlTemplate) {
          assets.push({
            urn,
            urlTemplate: meta.urlTemplate,
            ...(meta.width != null && meta.width >= 0 ? { width: meta.width } : undefined),
            ...(meta.height != null && meta.height >= 0 ? { height: meta.height } : undefined),
            ...(meta.singletonCapable === true ? { singletonCapable: true } : undefined),
            ...(Array.isArray(meta.paramKeysOmitWhenEmpty) && meta.paramKeysOmitWhenEmpty.length > 0 ? { paramKeysOmitWhenEmpty: meta.paramKeysOmitWhenEmpty } : undefined),
            ...(Array.isArray(meta.paramKeysFlag) && meta.paramKeysFlag.length > 0 ? { paramKeysFlag: meta.paramKeysFlag } : undefined),
            ...(meta.fixedDefaultConfig && Object.keys(meta.fixedDefaultConfig).length > 0 ? { fixedDefaultConfig: meta.fixedDefaultConfig } : undefined),
            ...(meta.defaultConfig && Object.keys(meta.defaultConfig).length > 0 ? { defaultConfig: meta.defaultConfig } : undefined)
          })
        }
        continue
      }
      if (hlsVideoUrns.has(urn)) {
        const meta = hlsVideoAssetMetadata![urn]
        if (meta?.hlsUrl) {
          assets.push({ urn, metadata: { hlsUrl: meta.hlsUrl, posterUrl: meta.posterUrl } })
        }
        continue
      }
      const cachedRaw = await this.dbService.getCachedAsset(urn)
      if (cachedRaw === null) {
        const recovered = await this._recoverCachedAssetFromHybridDisk(urn)
        if (recovered != null) {
          assets.push(recovered)
          continue
        }
        if (!localOnly) {
          assetsToDownload.add(urn)
        }
        continue
      }
      let cached = cachedRaw
      // P2: file-backed MP4 with a missing hybrid-cache file is a cache miss (repair path).
      if (isFileBackedCachedAsset(cached)) {
        const path = await hybridPlaybackFilePathIfPresent(cached.urn, cached.size)
        if (path == null) {
          addPlayerAssetCacheSentryBreadcrumb({
            phase: 'purge',
            context: 'fetchAssets',
            urn,
            reason: 'missing_hybrid_cache_file'
          })
          console.warn(`[AssetsService] Purging file-backed asset with missing disk file ${urn}`)
          await this.dbService.deleteCachedAsset(urn)
          if (!localOnly) {
            assetsToDownload.add(urn)
          }
          continue
        }
        cached = await this._dropRedundantIdbBlobIfNativeFileReady(cached, path)
      }
      else if (cached.blob != null && cachedAssetLooksLikeVideo(cached)) {
        const path = await hybridPlaybackFilePathIfPresent(cached.urn, cached.size)
        if (path != null) {
          cached = await this._dropRedundantIdbBlobIfNativeFileReady(cached, path)
        }
      }
      const assessment = await assessCachedBinaryForPlayback(cached)
      if (assessment.action === 'purge') {
        addPlayerAssetCacheSentryBreadcrumb({
          phase: 'purge',
          context: 'fetchAssets',
          urn,
          reason: assessment.reason
        })
        console.warn(`[AssetsService] Purging bad cached asset ${urn}: ${assessment.reason}`)
        await this.dbService.deleteCachedAsset(urn)
        if (!localOnly) {
          assetsToDownload.add(urn)
        }
        continue
      }
      if (assessment.action === 'repair') {
        const previousType = cached.type
        cached = await this.dbService.saveCachedAsset(assessment.asset)
        addPlayerAssetCacheSentryBreadcrumb({
          phase: 'repair_mime',
          context: 'fetchAssets',
          urn,
          previousType,
          newType: cached.type
        })
        console.info(`[AssetsService] Repaired cached asset MIME for ${urn} → ${cached.type}`)
      }
      if (!localOnly && revalidateCachedEtags && cached.etag) {
        assetsToRevalidate.set(urn, cached)
      }
      else {
        assets.push(cached)
      }
    }

    const allUrnsToFetch = localOnly
      ? new Set<string>()
      : new Set([...assetsToDownload, ...assetsToRevalidate.keys()])

    if (allUrnsToFetch.size > 0) {
      const jobId = uuidv4()
      const progressIndex: Record<string, number> = {}
      for (const urn of allUrnsToFetch) { progressIndex[urn] = 0 }
      this._downloadProgressIndex[jobId] = progressIndex
      this._updateDownloadProgress()
      try {
        const fetchOne = async (urn: string): Promise<ICachedAsset> => {
          const cachedEtag = assetsToRevalidate.get(urn)?.etag
          const asset = await this.apiClientService.fetchAssetBinary(urn, cachedEtag, ({ progress }) => {
            progressIndex[urn] = progress ?? 0
            this._updateDownloadProgress()
          })
          if (asset === null) {
            // 304 Not Modified — keep existing cached copy
            progressIndex[urn] = 1
            this._updateDownloadProgress()
            return assetsToRevalidate.get(urn)!
          }
          // Mark complete only after IDB persist so retries do not read 100% on a failed save.
          const saved = await this.dbService.saveCachedAsset(asset)
          progressIndex[urn] = 1
          this._updateDownloadProgress()
          return saved
        }

        let pending = new Set(allUrnsToFetch)
        let nextRoundDelayMs = 0
        for (let round = 0; round <= ASSET_BINARY_FETCH_EXTRA_ROUNDS; round++) {
          if (pending.size === 0) {
            break
          }
          if (round > 0) {
            const fallbackMs = ASSET_BINARY_FETCH_RETRY_DELAYS_MS[round - 1] ?? 8000
            await sleepMs(Math.max(fallbackMs, nextRoundDelayMs))
            nextRoundDelayMs = 0
          }
          const urnList = [...pending]
          const results = await mapAllSettledWithConcurrency(
            urnList,
            ASSET_BINARY_FETCH_CONCURRENCY,
            urn => fetchOne(urn)
          )
          const nextPending = new Set<string>()
          for (let i = 0; i < urnList.length; i++) {
            const urn = urnList[i]!
            const result = results[i]!
            if (result.status === 'fulfilled') {
              assets.push(result.value)
            }
            else {
              nextPending.add(urn)
              const reason = result.reason
              if (isRetryAfterAwareHttpError(reason)) {
                nextRoundDelayMs = Math.max(nextRoundDelayMs, reason.retryAfterMs!)
              }
              if (round === ASSET_BINARY_FETCH_EXTRA_ROUNDS) {
                const message = reason instanceof Error ? reason.message : String(reason)
                let cachedFallback = assetsToRevalidate.get(urn)
                /**
                 * Download-path misses (getCachedAsset null at classify time) never enter
                 * `assetsToRevalidate`, so a later IDB-ready blob used to be omitted forever after
                 * 403/network exhaust — queue still referenced the URN (#1574). Re-read cache once.
                 */
                if (cachedFallback == null) {
                  const reread = await this.dbService.getCachedAsset(urn)
                  if (reread != null) {
                    const assessment = await assessCachedBinaryForPlayback(reread)
                    if (assessment.action === 'keep') {
                      cachedFallback = reread
                    }
                    else if (assessment.action === 'repair') {
                      cachedFallback = await this.dbService.saveCachedAsset(assessment.asset)
                    }
                  }
                }
                if (cachedFallback) {
                  // Revalidation/download failed but a healthy cached copy exists — play it.
                  assets.push(cachedFallback)
                  addPlayerAssetCacheSentryBreadcrumb({
                    phase: 'revalidation_fallback',
                    context: 'fetchAssets',
                    urn,
                    error: message
                  })
                  console.warn(`[AssetsService] Revalidation failed for ${urn} after ${ASSET_BINARY_FETCH_EXTRA_ROUNDS + 1} attempt(s); using cached copy:`, message)
                }
                else {
                  console.warn(`[AssetsService] fetchAssetBinary failed for ${urn} after ${ASSET_BINARY_FETCH_EXTRA_ROUNDS + 1} attempt(s):`, message)
                }
              }
            }
          }
          pending = nextPending
        }
      }
      finally {
        delete this._downloadProgressIndex[jobId]
        this._updateDownloadProgress()
      }
    }

    return assets
  }

  /**
   * Scan every IndexedDB binary cache entry: drop unusable blobs and fix wrong `Content-Type`
   * (e.g. `application/octet-stream` for PNG). Use from debug UI or after diagnosing bad local cache.
   */
  async scanAndRepairAllCachedBinaryAssets(): Promise<{ purgedUrns: string[], repairedUrns: string[] }> {
    const all = await this.dbService.listAllCachedAssets()
    const purgedUrns: string[] = []
    const repairedUrns: string[] = []
    for (const asset of all) {
      const assessment = await assessCachedBinaryForPlayback(asset)
      if (assessment.action === 'purge') {
        addPlayerAssetCacheSentryBreadcrumb({
          phase: 'purge',
          context: 'full_scan',
          urn: asset.urn,
          reason: assessment.reason
        })
        console.warn(`[AssetsService] Cache scan: purging ${asset.urn}: ${assessment.reason}`)
        await this.dbService.deleteCachedAsset(asset.urn)
        purgedUrns.push(asset.urn)
        continue
      }
      if (assessment.action === 'repair') {
        addPlayerAssetCacheSentryBreadcrumb({
          phase: 'repair_mime',
          context: 'full_scan',
          urn: asset.urn,
          previousType: asset.type,
          newType: assessment.asset.type
        })
        await this.dbService.saveCachedAsset(assessment.asset)
        repairedUrns.push(asset.urn)
      }
    }
    if (purgedUrns.length > 0 || repairedUrns.length > 0) {
      addPlayerAssetCacheSentryBreadcrumb({
        phase: 'full_scan_summary',
        purgedCount: purgedUrns.length,
        repairedCount: repairedUrns.length
      })
    }
    return { purgedUrns, repairedUrns }
  }

  private async fetchLocalAsset(assetKey: string): Promise<ICachedAsset> {
    let asset = await this.dbService.getCachedAsset(assetKey)
    if (asset === null) {
      const { blob, etag } = await fetch(assetKey).then(async (response) => {
        const blob = await response.blob()
        const etag = response.headers.get('etag') ?? ''
        return { blob, etag }
      })
      asset = {
        urn: assetKey,
        blob,
        lastAccess: new Date(),
        size: blob.size,
        type: blob.type,
        etag
      }
      await this.dbService.saveCachedAsset(asset)
    }
    return asset
  }
}

export type TAssetsService = AssetsService
